import React, { useContext, useState } from "react";
import styled, { ThemeContext } from "styled-components";
import WBCCollapse from "../elements/WBCCollapse";
import RosterContext from "../../contexts/RosterContext";
import WBCYearsContext from "../../contexts/WBCYearsContext";
import Infield from "./final/Infield";
import Outfield from "./final/Outfield";
import Pitchers from "./final/Pitchers";
import Catchers from "./final/Catchers";
import AuthContext from "../../contexts/AuthContext";
import SelectedTeamContext from "../../contexts/SelectedTeamContext";
import GlobalModalContext from "../../contexts/GlobalModalContext";
import FinalizedStatusContext from "../../contexts/FinalizedStatusContext";
import TournamentTypeContext from "../../contexts/TournamentTypeContext";
import ExportApi from "../../httpClients/ExportApi";
import { FinalizeWrapper, FinalizeButton } from "../roster/RosterStyles";
import { FINAL_ROSTER } from "../../constants/RosterListConstants";
import { useAlert } from "../hooks/useAlert";
import useDownloadFile from "../hooks/useDownloadFile";
import { MAIN_ID, QUALIFYING, QUALIFYING_ID } from "../../constants/TournamentTypeConstants";
import DesignatedPitchersTable from "./final/DesignatedPitchersTable";
import CustomDownloadModal from "../shared/CustomDownloadModal";
import { FINAL_ID } from "../../constants/TournamentStatusConstants";
import { SubtitleText, StatusText } from "../elements/CommonStyledComponents";

const Container = styled.div`
  margin-bottom: 18px;
`;

const RosterContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;
const DesignatedPitchersContainer = styled.div`
  margin-top: 18px;
  width: 24%;
  min-width: 200px;
`;
const EXCEL = "Excel";
const PDF = "PDF";

const FinalRoster = () => {
  // context(s)
  const theme = useContext(ThemeContext);
  const authContext = useContext(AuthContext);
  const rosterContext = useContext(RosterContext);
  const { selectedWbcYear, selectedYear } = useContext(WBCYearsContext);
  const selectedTeamContext = useContext(SelectedTeamContext);
  const globalModalContext = useContext(GlobalModalContext);
  const finalizedStatusContext = useContext(FinalizedStatusContext);
  const tournamentType = useContext(TournamentTypeContext);
  const [openDownloadModal, setOpenDownloadModal] = useState(false);

  // variable(s)
  const { dispatch: rosterDispatch } = rosterContext;
  const { final, finalIsOpen } = rosterContext.state;
  const { isWBCIadmin, isFedAdmin, isTeamCoordinator } = authContext.state;
  const { team } = selectedTeamContext.state;
  const { finalizedAt, finalized } = finalizedStatusContext.state;
  const size = Object.values(final).reduce((pv, c) => pv + c.length, 0);
  const settings = (selectedWbcYear && selectedWbcYear[`${tournamentType}TournamentSettings`]) || null;
  const profileIds = Object.values(final)
    ?.flat()
    ?.map(player => player?.profileId);

  // hook(s)
  const showAlert = useAlert();
  const downloadFile = useDownloadFile();

  const canEdit = isWBCIadmin || isFedAdmin || isTeamCoordinator;
  const rosterType = FINAL_ROSTER;

  // function(s)
  const finalizeRoster = () => {
    globalModalContext.dispatch({
      type: "openFinalizeRosterModal",
      headerText: `Finalize ${rosterType}`,
      content: { team, prefix: tournamentType, rosterType }
    });
  };

  const exportExcel = () => {
    setOpenDownloadModal(true);
  };

  const exportPDF = () => {
    const tournamentTypeId = tournamentType === QUALIFYING ? QUALIFYING_ID : MAIN_ID;
    ExportApi.exportFinalRosters(selectedYear, tournamentTypeId, team.teamId)
      .then((res, headers) => {
        downloadFile(res, headers);
      })
      .catch(() => {
        showAlert("Error exporting final roster to PDF", "danger");
      });
  };

  const clickedFinalCollapse = isOpen => {
    rosterDispatch({
      type: "setFinalIsOpen",
      isOpen
    });
  };

  return (
    settings && (
      <Container>
        <WBCCollapse
          exportable
          exportableMenu={[
            { label: EXCEL, onClick: () => exportExcel() },
            { label: PDF, onClick: () => exportPDF() }
          ]}
          title={`Final (${size})`}
          // subTitle={`${settings.rosterNumbers.finalRosterMax} player max`}
          startsOpen={finalIsOpen}
          exportFn={exportExcel}
          openCloseCallback={clickedFinalCollapse}
        >
          {canEdit && (
            <SubtitleText>
              Drag players into position slots from the provisional roster. Players cannot be added to a Final Roster
              until their Eligibility is{" "}
              <StatusText
                bgColor={theme.eligibilityStatus.approved.background}
                color={theme.eligibilityStatus.approved.color}
              >
                Approved
              </StatusText>
            </SubtitleText>
          )}
          <RosterContainer>
            <Catchers settings={settings.positions} />
            <Pitchers settings={settings.positions} />
            <Infield settings={settings.positions} />
            <Outfield settings={settings.positions} />
          </RosterContainer>
        </WBCCollapse>
        {isWBCIadmin || isTeamCoordinator || (isFedAdmin && !finalized) ? (
          <>
            <FinalizeWrapper>
              <FinalizeButton
                onClick={event => {
                  event.stopPropagation();
                  finalizeRoster();
                }}
              >
                Finalize {rosterType}
              </FinalizeButton>
            </FinalizeWrapper>
            <FinalizeWrapper>{!!finalizedAt && <div>Finalized on {finalizedAt} </div>}</FinalizeWrapper>
          </>
        ) : null}
        <DesignatedPitchersContainer>
          <DesignatedPitchersTable />
        </DesignatedPitchersContainer>
        <CustomDownloadModal
          profileIds={profileIds}
          tournamentType={tournamentType === QUALIFYING ? QUALIFYING_ID : MAIN_ID}
          fedTeam={team?.teamId}
          federation={team?.label}
          rosterType={FINAL_ID}
          rosterName={FINAL_ROSTER}
          isOpen={openDownloadModal}
          onClose={() => setOpenDownloadModal(false)}
        />
      </Container>
    )
  );
};

export default FinalRoster;
